import { Tooltip } from "@/base";
import {
    Button,
    Calendar,
    cn,
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@heroui/react";
import { CalendarDate, getLocalTimeZone, today } from "@internationalized/date";
import { formatDate } from "@sr-partner-portal/common";
import {
    IconAlertCircle,
    IconPercentage0,
    IconPercentage50,
    IconPercentage60,
    IconPercentage75,
    IconPercentage90,
    IconTrash,
} from "@tabler/icons-react";
import { differenceInCalendarDays } from "date-fns";
import { toZonedTime } from "date-fns-tz";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";

interface DateChipProps {
    dateValue?: string | Date;
    editable?: boolean;
    dateFormat?: string;
    isDeadline?: boolean;
    isClearable?: boolean;
    size?: "sm" | "md" | "lg";
    label?: string;
    allowFutureDates?: boolean;
    isDisabled?: boolean;
    onValueChange?: (value: Date) => void;
    onOpenChange?: (isOpen: boolean) => void;
}

const toCalendarDate = (
    value: string | Date | undefined,
): CalendarDate | undefined => {
    if (!value) {
        return undefined;
    }

    const date = value instanceof Date ? value : new Date(value);

    if (Number.isNaN(date)) {
        return undefined;
    }

    return new CalendarDate(
        date.getFullYear(),
        date.getMonth() + 1,
        date.getDate(),
    );
};

const TimeIcon = ({ dayDifference }: { dayDifference: number }) => {
    const iconProps = {
        stroke: 1.5,
        size: 18,
        className: "text-default-500",
    };

    if (dayDifference < 0) {
        return (
            <Tooltip content="Überfällig">
                <IconAlertCircle {...iconProps} className="text-danger" />
            </Tooltip>
        );
    }

    if (dayDifference === 0) {
        return (
            <Tooltip content="Heute">
                <IconAlertCircle {...iconProps} className="text-warning" />
            </Tooltip>
        );
    }

    if (dayDifference === 1) {
        return (
            <Tooltip content="Morgen">
                <IconPercentage90 {...iconProps} />
            </Tooltip>
        );
    }

    if (dayDifference === 2) {
        return (
            <Tooltip content="Noch 2 Tage">
                <IconPercentage75 {...iconProps} />
            </Tooltip>
        );
    }

    if (dayDifference === 3) {
        return (
            <Tooltip content="Noch 3 Tage">
                <IconPercentage60 {...iconProps} />
            </Tooltip>
        );
    }

    if (dayDifference === 4) {
        return (
            <Tooltip content="Noch 4 Tage">
                <IconPercentage50 {...iconProps} />
            </Tooltip>
        );
    }

    return (
        <Tooltip content={`Noch ${dayDifference} Tage`}>
            <IconPercentage0 {...iconProps} />
        </Tooltip>
    );
};

const DateChip = memo<DateChipProps>(
    ({
        label,
        dateValue,
        editable = false,
        dateFormat = "dd. LLL.",
        isDeadline = false,
        isClearable = true,
        allowFutureDates = false,
        size,
        isDisabled,
        onValueChange,
        onOpenChange,
    }) => {
        const [isOpen, setIsOpen] = useState(false);

        const defaultValue = useMemo(
            () => toCalendarDate(dateValue),
            [dateValue],
        );

        const [value, setValue] = useState(toCalendarDate(dateValue));

        const escapePressed = useRef(false);

        const oldIsOpen = useRef(isOpen);

        const handleOnChange = useCallback((newValue: CalendarDate) => {
            setValue(newValue);
            setIsOpen(false);
        }, []);

        useEffect(() => {
            if (oldIsOpen.current === isOpen) {
                return;
            }

            oldIsOpen.current = isOpen;

            if (escapePressed.current) {
                escapePressed.current = false;
            } else if (
                !isOpen &&
                ((value && defaultValue?.compare(value) !== 0) ||
                    value === null)
            ) {
                onValueChange(value ? value.toDate(getLocalTimeZone()) : null);
            }

            onOpenChange?.(isOpen);
        }, [isOpen, onOpenChange, value, defaultValue, onValueChange]);

        const dayDifference = useMemo(() => {
            const today = toZonedTime(new Date(), getLocalTimeZone());
            const targetDate = toZonedTime(
                defaultValue?.toDate(getLocalTimeZone()),
                getLocalTimeZone(),
            );

            return differenceInCalendarDays(targetDate, today);
        }, [defaultValue]);

        const formattedDate = useMemo(
            () =>
                formatDate(
                    defaultValue?.toDate(getLocalTimeZone()),
                    "EEE dd. LLL. yyyy",
                ),
            [defaultValue],
        );

        const content = useMemo(
            () => (
                <div
                    className={cn(
                        "flex w-full items-center gap-1 rounded-md px-2 py-1",
                        isDeadline && dayDifference < 1 && "bg-warning-100",
                        isDeadline && dayDifference < 0 && "bg-danger-50",
                        size === "sm" && "px-2 py-0 text-sm",
                    )}
                >
                    {label}
                    {isDeadline && !Number.isNaN(dayDifference) && (
                        <TimeIcon dayDifference={dayDifference} />
                    )}
                    <Tooltip
                        classNames={{
                            base: "pointer-events-none",
                            content:
                                "truncate break-all whitespace-pre-line px-5 py-4",
                        }}
                        delay={200}
                        content={formattedDate}
                    >
                        <button
                            type="button"
                            className="select-none truncate text-left cursor-pointer"
                            disabled={isDisabled || !editable}
                            onClick={
                                editable && !isDisabled
                                    ? () => setIsOpen(true)
                                    : undefined
                            }
                        >
                            {formatDate(dateValue, dateFormat) || "–"}
                        </button>
                    </Tooltip>
                </div>
            ),
            [
                dateValue,
                editable,
                dateFormat,
                dayDifference,
                isDeadline,
                formattedDate,
                isDisabled,
            ],
        );

        return (
            <div className="flex h-8 items-center" role="menuitem" tabIndex={0}>
                <Popover
                    isOpen={isOpen}
                    onOpenChange={(open) => setIsOpen(open)}
                    classNames={{
                        content: "p-0",
                    }}
                    offset={-42} // position on top of text
                    placement="bottom"
                >
                    <PopoverTrigger>{content}</PopoverTrigger>
                    <PopoverContent>
                        <Calendar
                            autoFocus={true}
                            // @ts-ignore: Looks like some issue in nextui
                            value={value}
                            className="**:data-[today='true']:bg-primary-50"
                            maxValue={
                                allowFutureDates
                                    ? undefined
                                    : today(getLocalTimeZone())
                            }
                            onKeyDown={(event) => {
                                // apply data when pressing enter but not when pressing shift enter
                                if (event.key === "Enter" && !event.shiftKey) {
                                    event.preventDefault();
                                    setIsOpen(false);
                                } else if (event.key === "Escape") {
                                    escapePressed.current = true;
                                    handleOnChange(defaultValue);
                                }
                            }}
                            onChange={handleOnChange}
                            bottomContent={
                                isClearable && (
                                    <div className="mb-4 flex w-full items-center justify-end gap-2 px-4">
                                        <Button
                                            onPress={() => handleOnChange(null)}
                                            variant="flat"
                                            isIconOnly={true}
                                            size="sm"
                                        >
                                            <IconTrash stroke={1.5} size={18} />
                                        </Button>
                                    </div>
                                )
                            }
                        />
                    </PopoverContent>
                </Popover>
            </div>
        );
    },
);

export { DateChip };
